import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, EventEmitter, inject, Input, OnInit, OnDestroy, Output, signal } from '@angular/core';
import { faCreditCard } from '@fortawesome/free-solid-svg-icons';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { ICountry } from '../../../../models/ICountry';
import { IState } from '../../../../models/IState';
import { ApiService } from '../../../../services/api.service';
import { CustomValidators } from '../../../../shared/validators/custom-validators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AuthService } from '../../../../services/authentication/auth.service';
import { RecaptchaLoaderService } from '../../../../services/recaptcha-loader.service';
import { CreditCardsService } from '../../../../services/bidder-community/credit-cards/credit-cards.service';
import { Subscription , distinctUntilChanged, timeout } from 'rxjs';
import { ICreditCardDTO } from '../../../../models/ICreditCardDTO';
import { ICreditCardRequest } from '../../../../models/ICreditCardRequest';
import { ToastMessageService } from '../../../../services/toast-message/toast-message.service';
import { ShippingAddressService } from '../../../../services/bidder-community/shipping-address/shipping-address.service';
import { IShippingAddressDTO } from '../../../../models/IShippingAddressDTO ';
import { ProgressSpinner } from "primeng/progressspinner";
import { InputRestrictDirective } from '../../../../directives/input-restrict/input-restrict.directive';


interface EditModeData {
  isWeb: boolean;
  value: boolean;
}
interface SelectOption {
  name: string;
  value: string;
}

@Component({
  selector: 'app-credit-cards-info-form',
  imports: [Select, CommonModule, InputTextModule, FormsModule, ReactiveFormsModule, ButtonModule, ProgressSpinner, InputRestrictDirective],
  templateUrl: './credit-cards-info-form.component.html',
  styleUrl: './credit-cards-info-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CreditCardsInfoFormComponent implements OnInit, OnDestroy {
  
  private readonly creditCardsService = inject(CreditCardsService);
  private readonly oidcSecurityService = inject(OidcSecurityService);
  private readonly authService = inject(AuthService);
  private readonly recaptchaLoader = inject(RecaptchaLoaderService);
  private readonly messageService = inject(ToastMessageService);
  private readonly shippingAddressService = inject(ShippingAddressService);

 apiService = inject(ApiService);

 countries: ICountry[] = [];
 states: IState[] = [];


  form: FormGroup;

  @Input() id: number | null = null;
  @Input() isEditing!:boolean ; 
  @Input() isNewCard!:boolean ; 
  @Input() isSmallScreen!:boolean ; 


  @Input() isLoadingCardList!:boolean ; 
 

  @Input() selectedCard! : ICreditCardDTO | null ; 
  @Output() selectedCardChange = new EventEmitter<ICreditCardDTO | null>();

  @Input() creditCardsList: ICreditCardDTO[] | null = null;
  @Output() creditCardsListChange = new EventEmitter<ICreditCardDTO[] | null>();


  @Output() turnEditMode: EventEmitter<EditModeData> = new EventEmitter();
  @Output() printCardHolderName: EventEmitter<String> = new EventEmitter();
  @Output() printCardNumber: EventEmitter<String> = new EventEmitter();
  @Output() printMonth: EventEmitter<String> = new EventEmitter();
  @Output() printYear: EventEmitter<String> = new EventEmitter();
  @Output() isLoadingCardListChange = new EventEmitter<boolean>();

  
  isAuthenticated = this.oidcSecurityService.authenticated;
  authSubscription!: Subscription;

  labelState!: string;
  labelPostalCode!: string;
  placeHolderState!: string;
  placeHolderPostalCode!: string;
  defaultCountry!: string;

  isLoadingInfoSection = false;
  isSubmitting = signal<boolean>(false);

  homePageSignal = this.apiService.homePageSignal;

  shippingAddressInfo: IShippingAddressDTO | null = null;
  
  months: SelectOption[] = [
    { "value" : "1", "name":"01 - January"},
    { "value" : "2", "name":"02 - February"},
    { "value" : "3", "name":"03 - March"},
    { "value" : "4", "name":"04 - April"},
    { "value" : "5", "name":"05 - May"},
    { "value" : "6", "name":"06 - June"},
    { "value" : "7", "name":"07 - July"},
    { "value" : "8", "name":"08 - August"},
    { "value" : "9", "name":"09 - September"},
    { "value" : "10", "name":"10 - October"},
    { "value" : "11", "name":"11 - November"},
    { "value" : "12", "name":"12 - December"},
  ];

  years: SelectOption[] = []; 

  protected icons = {
    faCreditCard: faCreditCard
  }

  constructor(private fb: FormBuilder, private cdr: ChangeDetectorRef){
    this.form = this.fb.group({
      cardHolder: ['', [CustomValidators.required('Card Holder Name')]],
      cardNumber: ['', [CustomValidators.required('Card Number'), CustomValidators.numeric('Card Number')]],
      expirationMonth: ['', CustomValidators.required('Expiration Date')],
      expirationYear: ['', CustomValidators.required('Expiration Date')],
      country: ['', CustomValidators.required],
      billingAddress: ['', CustomValidators.required('Address')],
      billingAddress2: '',
      zipCode: ['', [CustomValidators.required('ZIP Code'), CustomValidators.zipCodeValidator('country')]],
      city: ['', CustomValidators.required('City')],
      state: ['', [CustomValidators.required(''), CustomValidators.atLeastOneLetter('Region')]],
    });

    effect(() => {
      const countries = this.apiService.countrySignal();
      if (countries)
        this.countries = countries;

      const states = this.apiService.stateSignal();
      if (states && states.length > 0) {
        this.states = states;
      } else {
        this.states = [];
        this.form.get('state')?.setValue('');
      }
    });

    const currentYear = new Date().getFullYear(); 
    for (let i = 0; i <= 20; i++) {
      const yearNumber = currentYear + i;
      var year:SelectOption = {"name":yearNumber.toString(), "value":yearNumber.toString()}
      this.years.push(year)
    }
  }

  ngOnDestroy(): void {
    this.recaptchaLoader.unload();
  }

  ngOnInit(): void {
    this.recaptchaLoader.load();

    this.onCountries();

    setTimeout(() => {
      if (this.homePageSignal()) {
        this.defaultCountry = this.homePageSignal()!.orgCountryCode;
      }
    });

    this.form.get('country')?.setValue(this.defaultCountry ? this.defaultCountry : 'US');
    this.onLabelState(this.defaultCountry);
    this.onLabelPostalCode(this.defaultCountry);
    this.onStates(this.defaultCountry);

    if (this.defaultCountry === 'US') {
      this.labelPostalCode = 'Zip Code'
    } else {
      this.labelPostalCode = 'Postal Code'

    }

    //country select event
    this.form.get('country')?.valueChanges.subscribe(country => {
      if(this.form.dirty){
        this.form.patchValue({
          state: '',
        });
      }
      this.onLabelState(country);
      this.onLabelPostalCode(country);
      this.onStates(country);

      // Reset fields and clear validators 
      this.form.get('state')?.clearValidators();
      this.form.get('zipCode')?.clearValidators();


      this.form.get('state')?.setValidators([
        CustomValidators.required(this.labelState),
        CustomValidators.atLeastOneLetter(this.labelState)
      ]);
      this.form.get('state')?.updateValueAndValidity();


      this.labelPostalCode = country === 'US' ? 'Zip Code' : 'Postal Code';

      this.form.get('zipCode')?.setValidators([
        CustomValidators.required(this.labelPostalCode),
        CustomValidators.zipCodeValidator(this.form.get("country")?.getRawValue())
      ]);
      this.form.get('zipCode')?.updateValueAndValidity();
      

    });

    this.form.get('cardHolder')?.valueChanges.subscribe(cardHolder => {
      this.printCardHolderNamefunc(cardHolder);
    });
    this.form.get('cardNumber')?.valueChanges.subscribe(cardNumber => {
      this.printCardNumberfunc(cardNumber);
    });
    this.form.get('expirationMonth')?.valueChanges.subscribe(month => {
      this.printMonthfunc(month);
    });
    this.form.get('expirationYear')?.valueChanges.subscribe(year => {
      this.printYearfunc(year);
    });

    this.authSubscription = this.authService.isAuthenticated$.pipe(distinctUntilChanged()).subscribe(isAuth => {
      if (isAuth) {
        this.getCreditCardsList();
        this.getShippingAddressInfo();
      }
    });

  }

  printCardHolderNamefunc(data: String){
    this.printCardHolderName.emit(data);
  }
  printCardNumberfunc(data: String){
    this.printCardNumber.emit(data);
  }
  printMonthfunc(data: String){
    this.printMonth.emit(data);
  }
  printYearfunc(data: String){
    this.printYear.emit(data);
  }


  onLabelPostalCode(country: string) {
    switch (country) {
      case 'US':
        this.labelPostalCode = 'Zip Code'
        this.placeHolderPostalCode = 'Enter your Zip Code'
        break;
      default:
        this.labelPostalCode = 'Postal Code'
        this.placeHolderPostalCode = 'Enter your Postal Code'
        break;
    }
  }

  onLabelState(country: string) {
    switch (country) {
      case 'US':
        this.labelState = 'State'
        this.placeHolderState = 'Select State'
        break;
      case 'CA':
        this.labelState = 'Province'
        this.placeHolderState = 'Select Province'
        break;
      case 'AU':
        this.labelState = 'State'
        this.placeHolderState = 'Select State'
        break;
      default:
        this.labelState = 'Region'
        this.placeHolderState = 'Enter your region'
        break;
    }
    
   

  }

  onCountries() {
    if (typeof window !== 'undefined') {
      this.apiService.getCountries().subscribe();
    }
  }

  onStates(countrySelected: string) {
    if (typeof window !== 'undefined') {
      const form = this.form.value;
      // const countrySelected = form.get('country')?.value;

      this.apiService.getStates(countrySelected != '' ? countrySelected : 'US').subscribe(states => {
        this.form.get('state')!.setValue(this.selectedCard?.billingAddress.region);
        setTimeout(() => {
          if(!this.form.dirty && this.selectedCard && this.selectedCard!.billingAddress){
            this.form.patchValue({
              state: this.selectedCard!.billingAddress.region,
            });
          }
        },100);
      });

    }
  }

  turnEditModefunc(isWeb: boolean, value: boolean){
    if(!this.isNewCard){
      this.form.get('cardNumber')?.disable();
    }else{
      this.form.get('cardNumber')?.enable();
    }
    const data: EditModeData = { isWeb: isWeb, value: value };
    this.turnEditMode.emit(data);
  }

  async addCardInfo(){
    this.isSubmitting.set(true);
    this.cdr.detectChanges();
    let newCard:ICreditCardRequest = {
      number : this.form.get("cardNumber")?.getRawValue(),
      name : this.form.get("cardHolder")?.getRawValue(),
      expMonth : this.form.get("expirationMonth")?.getRawValue(),
      expYear : this.form.get("expirationYear")?.getRawValue(),
      country : this.form.get("country")?.getRawValue(),
      address1 : this.form.get("billingAddress")?.getRawValue(),
      address2 : this.form.get("billingAddress2")?.getRawValue(),
      postalCode : this.form.get("zipCode")?.getRawValue(),
      city : this.form.get("city")?.getRawValue(),
      region : this.form.get("state")?.getRawValue(),
      defaultCard: false,
      token: '12'
    };
    (await this.authService.addCreditCard(newCard)).subscribe({
      next: (result) => { 
        this.isSubmitting.set(false);
        newCard = JSON.parse(result);
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.isNewCard = false;
        this.turnEditModefunc(!this.isSmallScreen,false);
        this.messageService.success(`Credit Card Added successfully`);
      },
      error: (err) => { 
        this.isSubmitting.set(false);
        this.messageService.error(err || 'unknown error');
      }
    })
  }

  updateCardInfo(){
    this.selectedCard!.cardHolderName = this.form.get("cardHolder")?.getRawValue();
    this.selectedCard!.expirationMonth = this.form.get("expirationMonth")?.getRawValue();
    this.selectedCard!.expirationYear = this.form.get("expirationYear")?.getRawValue();

    this.selectedCard!.billingAddress.countryCode = this.form.get("country")?.getRawValue();
    this.selectedCard!.billingAddress.address1 = this.form.get("billingAddress")?.getRawValue();
    this.selectedCard!.billingAddress.address2 = this.form.get("billingAddress2")?.getRawValue();
    this.selectedCard!.billingAddress.zipCode = this.form.get("zipCode")?.getRawValue();
    this.selectedCard!.billingAddress.city = this.form.get("city")?.getRawValue();
    this.selectedCard!.billingAddress.region = this.form.get("state")?.getRawValue();

    this.patchCreditCard(this.selectedCard!);
    this.isLoadingInfoSection = true;
  }


  //-- Data Retrieve
  getCreditCardsList() {
    this.creditCardsService.getCreditCards().subscribe(({
      next: () => {
        this.isLoadingCardList = false;
        this.isLoadingCardListChange.emit(this.isLoadingCardList);
        this.creditCardsList = this.creditCardsService.creditCardsListSignal();
        const next = this.creditCardsList;
        this.creditCardsListChange.emit(next);
        if (this.creditCardsList && this.creditCardsList.length>0 && (this.selectedCard == null || this.selectedCard!.creditCardInfo.id == 0)) {
          this.creditCardsList.forEach(card_i => {
            if(card_i.isDefaultCard){
              this.selectedCard = card_i;
              return;
            }
          });
          if(!this.selectedCard || this.selectedCard.creditCardInfo.id == 0){
            this.selectedCard = this.creditCardsList[0];
          }

        }
        this.selectedCardChange.emit(this.selectedCard);
        
        this.resetSelectedCardValues();
        
      },
      error: (err) => {
        console.log('Error retrieve data', err);
      }
    }))
  }
  
  //-- Patch + reset form
  patchCreditCard(card: ICreditCardDTO) {
    this.creditCardsService.patchCreditCard(card).subscribe(({
      next: (result) => {
        card = JSON.parse(result);
        this.form.markAsPristine();
        this.form.markAsUntouched();
        this.turnEditModefunc(!this.isSmallScreen,false);
        this.isLoadingInfoSection = false;
        
        this.messageService.success(`Credit Card Updated successfully`);
      },
      error: (err) => {
        this.messageService.error(err || 'unknown error');
        this.isLoadingInfoSection = false;
      }
    }))
  }

  //patch but not reset form
  setDefaultCreditCard(card: ICreditCardDTO) {
    this.creditCardsService.patchCreditCard(card).subscribe(({
      next: (result) => {
        card = JSON.parse(result);
        this.creditCardsList!.forEach(card_i => {
          if(card_i.creditCardInfo.id != card.creditCardInfo.id){
            card_i.isDefaultCard = false;
          }
        });
        this.creditCardsListChange.emit(this.creditCardsList);
        this.getCreditCardsList();
        this.messageService.success(`Default Credit Card updated successfully`);
      },
      error: (err) => {
        this.messageService.error(err || 'unknown error');
      }
    }))
  }

  //-- Shipping Address --//

  getShippingAddressInfo() {
    this.shippingAddressService.getShippingAddressInfo().subscribe(({
      next: () => {
        this.shippingAddressInfo = this.shippingAddressService.shippingAddressInfoSignal();
      },
      error: (err) => {
        console.error('Error retreve data', err);
      },
    }))
  }

  useShippingAddress(){
    this.selectedCard!.billingAddress.countryCode = this.shippingAddressInfo!.countryCode;
    this.selectedCard!.billingAddress.address1 = this.shippingAddressInfo!.address1;
    this.selectedCard!.billingAddress.address2 = '';
    this.selectedCard!.billingAddress.zipCode = this.shippingAddressInfo!.zipCode;
    this.selectedCard!.billingAddress.city = this.shippingAddressInfo!.city;
    
    this.form.patchValue({
      country: this.shippingAddressInfo?.countryCode,
      billingAddress: this.shippingAddressInfo?.address1,
      billingAddress2: '',
      city: this.shippingAddressInfo?.city,
      zipCode: this.shippingAddressInfo?.zipCode,
    });

    setTimeout(() => {
      this.selectedCard!.billingAddress.region = this.shippingAddressInfo!.region;
      this.form.patchValue({
        state: this.shippingAddressInfo?.region,
      });
      this.form.updateValueAndValidity();
      this.form.markAsDirty();
    },400);

  }

  resetSelectedCardValues(){

    this.form.patchValue({
      cardNumber: '************'+this.selectedCard!.creditCardInfo.lastDigits,
      cardHolder: this.selectedCard!.cardHolderName,
      expirationMonth: this.selectedCard!.expirationMonth.toString(),
      expirationYear: this.selectedCard!.expirationYear.toString(),
      //Billing Address
      country: this.selectedCard!.billingAddress.countryCode,
      billingAddress: this.selectedCard!.billingAddress.address1,
      billingAddress2: this.selectedCard!.billingAddress.address2,
      zipCode: this.selectedCard!.billingAddress.zipCode,
      city: this.selectedCard!.billingAddress.city,
    });
    
    this.cdr.detectChanges();
  } 

      


}
